// FEED buff — in-memory only (does not persist across reload by design;
// it's a tactical lever the player engages during a session).
//
// Re-buying while active extends the existing timer by another duration.
//
// FEED also doubles as cat maintenance: the time since `lastFedAt` drives
// the cats' hunger state (healthy → skinny → dying). Buying FEED resets the
// hunger clock back to "just fed". A periodic tick re-evaluates the state
// and, in the dying state, rolls for random starvation deaths.

import { Events, on, emit } from '../ui/eventBus.js'
import { FeedConfig, HungerConfig } from '../config/balance.js'
import * as season from './seasonManager.js'
import * as adventureManager from './adventureManager.js'
import * as hospital from './hospital.js'
import { hungerStage, legacyHungerState } from './hungerRules.js'

let expiresAt = 0
let lastFedAt = Date.now()
let lastEmittedState = 'healthy'
let lastEmittedStage = 'healthy'
let hungerTimer = null

export function init() {
  on(Events.FeedBuyRequested, () => buy())
  lastFedAt = Date.now()
  lastEmittedState = 'healthy'
  lastEmittedStage = 'healthy'
  if (hungerTimer) clearInterval(hungerTimer)
  hungerTimer = setInterval(tickHunger, HungerConfig.deathTickMs)
}

// Cost to feed the whole kingdom — scales with how many citizens you have
// (you're feeding each cat). Floors at FeedConfig.cost.
export function feedCost() {
  return Math.max(FeedConfig.cost, FeedConfig.costPerCitizen * season.totalCitizens())
}

function buy() {
  const cost = feedCost()
  if (season.getWealth() < cost) return false
  if (!season.spendWealth(cost)) return false
  const base = Math.max(expiresAt, Date.now())
  expiresAt = base + FeedConfig.durationMs
  // Feeding also restores the hunger clock — cats go back to "well fed".
  lastFedAt = Date.now()
  emit(Events.FeedActivated, { expiresAt })
  if (lastEmittedStage !== 'healthy') {
    lastEmittedState = 'healthy'
    lastEmittedStage = 'healthy'
    emit(Events.CatHungerChange, { state: 'healthy', stage: 'healthy' })
  }
  return true
}

export function getRateMultiplier() {
  return Date.now() < expiresAt ? FeedConfig.goonRateBonus : 0
}

export function isActive() {
  return Date.now() < expiresAt
}

export function remainingMs() {
  return Math.max(0, expiresAt - Date.now())
}

// Time since the last FEED, as the hunger clock sees it. Adventure home-risk: while the cat is away, hunger advances faster
// (citizens can't be reliably fed) — the active adventure's feedDrainMult stretches the elapsed time so thresholds trip sooner.
export function hungerElapsedMs() {
  let mult = 1
  try {
    const def = adventureManager.currentDef?.()
    if (def?.dangerProfile?.feedDrainMult) mult = def.dangerProfile.feedDrainMult
  } catch { /* adventureManager not yet initialized — fine */ }
  return (Date.now() - lastFedAt) * mult
}

// The stage: healthy → hungry (thin) → caution (⚠) → critical (red cross) → hospital (admissions). Rules in hungerRules.js.
export function getHungerStage() { return hungerStage(hungerElapsedMs(), HungerConfig) }
// The older three-way reading: 'healthy' | 'skinny' | 'dying'.
export function getHungerState() { return legacyHungerState(getHungerStage()) }

function tickHunger() {
  const stage = getHungerStage()
  const state = legacyHungerState(stage)
  if (stage !== lastEmittedStage) {
    lastEmittedStage = stage
    lastEmittedState = state
    emit(Events.CatHungerChange, { state, stage })
  }
  // Nobody dies in the field any more: once admissions begin, one citizen at a time is taken to the Hospital (hospital.js),
  // where the player can still heal it — for a price — before it is lost.
  if (stage === 'hospital' && Math.random() < HungerConfig.admitChance) hospital.admitOne()
}

if (import.meta.env?.DEV && typeof window !== 'undefined') {
  window.__hunger = { stage: getHungerStage, elapsed: hungerElapsedMs, warp: (ms) => { lastFedAt -= ms; tickHunger() } } // dev/test: age the hunger clock by ms
}
